/**
 * Contrast, focus order, reduced motion, and the minimum-brightness palette (TASKS T43,
 * SPEC §2 `ui/a11y.ts`).
 *
 * Usage scenario 1 is why this file has teeth: 3:40 a.m., room dark, screen at minimum brightness,
 * one thumb, sound off. A palette that clears WCAG AA on an office monitor can be unreadable there,
 * because dropping the backlight compresses the whole luminance range toward black. So the floor
 * here is **AAA (7:1) for body text**, not AA, and `checkPalette()` is asserted by a unit test — the
 * palette cannot be dimmed without the build going red.
 *
 * Pure except for the three DOM helpers at the bottom, which take their document explicitly.
 */

// ---------------------------------------------------------------------------------------------
// Colour maths (WCAG 2.1 relative luminance)
// ---------------------------------------------------------------------------------------------

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** `#rgb` or `#rrggbb`. Throws on anything else: a mistyped colour must not silently become black. */
export function parseHex(hex: string): Rgb {
  const raw = hex.trim().replace(/^#/, '');
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) {
    throw new RangeError(`not a hex colour: ${JSON.stringify(hex)}`);
  }
  return {
    r: Number.parseInt(full.slice(0, 2), 16),
    g: Number.parseInt(full.slice(2, 4), 16),
    b: Number.parseInt(full.slice(4, 6), 16),
  };
}

/** The sRGB transfer function, inverted. WCAG 2.1 §relative luminance. */
function channel(value8: number): number {
  const c = value8 / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

export function relativeLuminance(color: string | Rgb): number {
  const { r, g, b } = typeof color === 'string' ? parseHex(color) : color;
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG contrast ratio, 1..21. Order-independent. */
export function contrastRatio(a: string | Rgb, b: string | Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

// ---------------------------------------------------------------------------------------------
// The palette
// ---------------------------------------------------------------------------------------------

/**
 * The single source of truth for every colour in the app.
 *
 * `base.css` restates these as custom properties rather than having them injected at runtime — a
 * JS-written palette flashes unstyled on a cold load, which on a phone at 3:40 a.m. is a white
 * screen in a dark room. The duplication is safe because `tests/unit/a11y.spec.ts` parses
 * `base.css` and fails if a single value has drifted from this object.
 */
export const PALETTE = Object.freeze({
  /**
   * Page background. Blue-black rather than neutral black.
   *
   * Raised from `#08090b` after a play session: *"I don't want it to just be a black background.
   * That's boring and makes it feel like a task."* A flat void reads as a form to be filled in. The
   * luminance is barely changed — this is still a dark room at minimum brightness — but a ground
   * with a hue in it has somewhere to sit against, and `bgDeep` gives it a gradient to sit in.
   */
  bg: '#0a0c14',
  /** The outer end of the page gradient. Darker than `bg`, so every text ratio only improves. */
  bgDeep: '#050609',
  /** Raised surfaces: the card panel. */
  surface: '#161a26',
  /** Raised *on* a surface: buttons, tiles, fields. Gives a control an edge to be seen by. */
  surfaceLift: '#212736',
  /** Borders and dividers on interactive components. Held to the 3:1 non-text floor. */
  line: '#6b7488',
  /** Primary text. */
  ink: '#f4f6fa',
  /** Secondary text. Still held to 7:1 — "dim" is a hierarchy, not permission to be unreadable. */
  inkDim: '#adb6c6',
  /** The one accent. Used for focus, the primary action, and nothing decorative. */
  accent: '#7fd1ff',
  /** Text on an accent-filled surface. */
  accentInk: '#04202c',
  /**
   * Amber and red exist for **component-level diagnostics only** (SPEC §8.1). They are never
   * applied to a summary figure and never to a judgement of the person: nothing in this game
   * colours the learner red for a bad week.
   */
  warn: '#f0b429',
  alarm: '#ff8a7a',

  /*
   * One hue per tier, so a ronda does not look like one long card.
   *
   * **Decorative only, and never load-bearing.** Every card says in words what it is asking for, so
   * nothing here is colour-only signalling — remove all six and the game reads identically. They are
   * held to the 7:1 text floor anyway, because the tier label is drawn in them and a "decorative"
   * colour that turns out to be text is how a palette rots.
   */
  tier1: '#c9a7ff',
  tier2: '#7fd1ff',
  tier3: '#66e0c0',
  tier4: '#ffc978',
  tier5: '#ff9fb5',
  tier6: '#9db8ff',
} as const);

/** The tier hues, by tier. Decorative; see `PALETTE`. */
export const TIER_COLOUR: Readonly<Record<1 | 2 | 3 | 4 | 5 | 6, string>> = Object.freeze({
  1: PALETTE.tier1,
  2: PALETTE.tier2,
  3: PALETTE.tier3,
  4: PALETTE.tier4,
  5: PALETTE.tier5,
  6: PALETTE.tier6,
});

export type PaletteToken = keyof typeof PALETTE;

/** CSS custom-property name for a token: `inkDim` → `--ink-dim`. */
export function cssVarName(token: PaletteToken): string {
  return `--${token.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`;
}

/**
 * WCAG AAA for body text. Deliberately above the AA 4.5 that most audits stop at, because the
 * primary reading condition for this app is a phone at minimum brightness (BRIEF usage scenario 1).
 */
export const MIN_CONTRAST_BODY = 7;

/** Non-text UI boundaries — button borders, input outlines, focus rings. WCAG 1.4.11. */
export const MIN_CONTRAST_UI = 3;

/** No text below this size, and no thin weights at all (BRIEF <platform_precedence>, iPhone). */
export const MIN_TEXT_PX = 15;

export interface ContrastPair {
  name: string;
  fg: PaletteToken;
  bg: PaletteToken;
  minimum: number;
}

/**
 * Every foreground/background combination the app actually paints.
 *
 * Enumerated by hand rather than by taking the cross product: most pairs are never rendered
 * together, and asserting on combinations nobody sees would either fail for nothing or force the
 * palette to be flatter than it needs to be.
 */
export const REQUIRED_CONTRAST: readonly ContrastPair[] = Object.freeze([
  { name: 'body text on the page', fg: 'ink', bg: 'bg', minimum: MIN_CONTRAST_BODY },
  { name: 'body text on a card', fg: 'ink', bg: 'surface', minimum: MIN_CONTRAST_BODY },
  { name: 'body text on a control', fg: 'ink', bg: 'surfaceLift', minimum: MIN_CONTRAST_BODY },
  { name: 'body text at the gradient edge', fg: 'ink', bg: 'bgDeep', minimum: MIN_CONTRAST_BODY },
  { name: 'secondary text on the page', fg: 'inkDim', bg: 'bg', minimum: MIN_CONTRAST_BODY },
  { name: 'secondary text on a card', fg: 'inkDim', bg: 'surface', minimum: MIN_CONTRAST_BODY },
  { name: 'secondary text on a control', fg: 'inkDim', bg: 'surfaceLift', minimum: MIN_CONTRAST_BODY },
  { name: 'secondary text at the gradient edge', fg: 'inkDim', bg: 'bgDeep', minimum: MIN_CONTRAST_BODY },
  { name: 'accent text on the page', fg: 'accent', bg: 'bg', minimum: MIN_CONTRAST_BODY },
  { name: 'label on the primary action', fg: 'accentInk', bg: 'accent', minimum: MIN_CONTRAST_BODY },
  { name: 'diagnostic amber on a card', fg: 'warn', bg: 'surface', minimum: MIN_CONTRAST_BODY },
  { name: 'diagnostic red on a card', fg: 'alarm', bg: 'surface', minimum: MIN_CONTRAST_BODY },
  { name: 'component border against the page', fg: 'line', bg: 'bg', minimum: MIN_CONTRAST_UI },
  { name: 'component border against a card', fg: 'line', bg: 'surface', minimum: MIN_CONTRAST_UI },
  { name: 'component border against a control', fg: 'line', bg: 'surfaceLift', minimum: MIN_CONTRAST_UI },
  { name: 'focus ring against the page', fg: 'accent', bg: 'bg', minimum: MIN_CONTRAST_UI },
  // The tier hues are decorative, and are still held to the *text* floor: the tier label is drawn in
  // them, and a "decorative" colour that turns out to be text is how a palette rots.
  { name: 'tier 1 label on a card', fg: 'tier1', bg: 'surface', minimum: MIN_CONTRAST_BODY },
  { name: 'tier 2 label on a card', fg: 'tier2', bg: 'surface', minimum: MIN_CONTRAST_BODY },
  { name: 'tier 3 label on a card', fg: 'tier3', bg: 'surface', minimum: MIN_CONTRAST_BODY },
  { name: 'tier 4 label on a card', fg: 'tier4', bg: 'surface', minimum: MIN_CONTRAST_BODY },
  { name: 'tier 5 label on a card', fg: 'tier5', bg: 'surface', minimum: MIN_CONTRAST_BODY },
  { name: 'tier 6 label on a card', fg: 'tier6', bg: 'surface', minimum: MIN_CONTRAST_BODY },
] as const);

export interface ContrastFailure extends ContrastPair {
  ratio: number;
}

/** Every pair that falls short. Empty means the palette is shippable. */
export function checkPalette(palette: Record<PaletteToken, string> = PALETTE): ContrastFailure[] {
  return REQUIRED_CONTRAST.filter(
    (pair) => contrastRatio(palette[pair.fg], palette[pair.bg]) < pair.minimum,
  ).map((pair) => ({ ...pair, ratio: contrastRatio(palette[pair.fg], palette[pair.bg]) }));
}

// ---------------------------------------------------------------------------------------------
// Motion
// ---------------------------------------------------------------------------------------------

/**
 * Reduced motion, read from the OS.
 *
 * `base.css` already kills CSS animation under this query. This exists for the canvas effects layer
 * (T58), which the stylesheet cannot reach, and it defaults to **true** where `matchMedia` is
 * missing: if we cannot tell, the safe answer is the calmer one.
 */
export function prefersReducedMotion(win: Window = window): boolean {
  if (typeof win.matchMedia !== 'function') return true;
  return win.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// ---------------------------------------------------------------------------------------------
// Live region
// ---------------------------------------------------------------------------------------------

/**
 * A polite live region, so feedback that appears silently is still announced.
 *
 * `polite` and not `assertive`: grading feedback interrupting a screen reader mid-word would be its
 * own kind of hostile, and nothing in this game is urgent — there is no timer the learner can lose.
 */
export class Announcer {
  private readonly node: HTMLElement;
  private last = '';

  constructor(doc: Document = document) {
    const existing = doc.getElementById('a11y-live');
    if (existing) {
      this.node = existing;
      return;
    }
    const node = doc.createElement('div');
    node.id = 'a11y-live';
    node.className = 'visually-hidden';
    node.setAttribute('role', 'status');
    node.setAttribute('aria-live', 'polite');
    node.setAttribute('aria-atomic', 'true');
    doc.body.append(node);
    this.node = node;
  }

  /**
   * Announce `message`. Repeating the same string re-announces it, because "Correct" twice in a row
   * is two real events and a screen reader that stays silent the second time is lying about the
   * first.
   */
  announce(message: string): void {
    this.node.textContent = message === this.last ? `${message} ` : message;
    this.last = message;
  }

  clear(): void {
    this.node.textContent = '';
    this.last = '';
  }
}

// ---------------------------------------------------------------------------------------------
// Focus
// ---------------------------------------------------------------------------------------------

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function focusableWithin(container: ParentNode): HTMLElement[] {
  return [...container.querySelectorAll<HTMLElement>(FOCUSABLE)];
}

/**
 * Move focus to a new card's primary action, if it has one.
 *
 * Two deliberate refusals.
 *
 * **It will not focus a non-primary control.** Falling back to "the first focusable thing" makes
 * Enter activate whatever happens to be first in the DOM — which on the run card is *End run and
 * bank*, turning the PC submit key into a quit key and contradicting SPEC §4, where Enter submits
 * and only Escape ends the run. A card with no primary action leaves focus where it was; Tab still
 * reaches everything.
 *
 * **It will not take focus off a field.** Stealing focus out of a text input mid-answer eats
 * keystrokes, and on iOS it dismisses the keyboard — which at 3:40 a.m. costs the whole item.
 */
export function focusPrimary(container: ParentNode, doc: Document = document): void {
  const active = doc.activeElement;
  if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) return;
  const primary = focusableWithin(container).find((node) => node.dataset['primary'] === 'true');
  primary?.focus();
}
